import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { CONTENT } from '../data/lempoleData';
import SignatureSVG from './SignatureSVG';

/**
 * DiarioModal — full "cellar diary" rendered inside an overlay.
 * Opening the modal never navigates, so closing it leaves the underlying
 * page scroll position untouched.
 */
export default function DiarioModal({ open, onClose, lang = 'it' }) {
  const d = (CONTENT[lang] || CONTENT.it).diario.annata;
  const scrollRef = useRef(null);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    // Save scroll position at open time. Locking scroll via `overflow: hidden`
    // on <html> does NOT reset the underlying scrollY, so no layout jump.
    const savedY = window.scrollY || window.pageYOffset || 0;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const prev = {
      htmlOverflow: document.documentElement.style.overflow,
      bodyOverflow: document.body.style.overflow,
      bodyPaddingRight: document.body.style.paddingRight,
    };
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    // Lenis has its own rAF loop that keeps scrolling the page even when
    // body/html overflow is hidden — so pause it while the modal is open.
    if (window.__lenis) window.__lenis.stop();
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    closeBtnRef.current && closeBtnRef.current.focus();

    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;

      // Focus trap: cycle focus among focusable elements inside the modal.
      const root = document.querySelector('[data-testid="diario-modal"]');
      if (!root) return;
      const focusables = Array.from(root.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )).filter((el) => {
        // Robust visibility check that works inside a position:fixed ancestor,
        // where `offsetParent` may be null even for visible elements.
        if (el.hasAttribute('inert') || el.getAttribute('aria-hidden') === 'true') return false;
        if (el.getClientRects().length === 0) return false;
        const cs = getComputedStyle(el);
        return cs.visibility !== 'hidden' && cs.display !== 'none';
      });
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.documentElement.style.overflow = prev.htmlOverflow;
      document.body.style.overflow = prev.bodyOverflow;
      document.body.style.paddingRight = prev.bodyPaddingRight;
      if (window.__lenis) window.__lenis.start();
      // Belt-and-braces: if any layout-shift moved the scroll, restore it.
      if (window.scrollY !== savedY) window.scrollTo(0, savedY);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="diario-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="diario-modal-title"
      data-testid="diario-modal"
    >
      <div
        className="diario-modal__backdrop"
        onClick={onClose}
        data-testid="diario-modal-backdrop"
      />

      <div
        className="diario-modal__scroll"
        ref={scrollRef}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        data-lenis-prevent
      >
        <button
          type="button"
          className="diario-modal__close"
          onClick={onClose}
          aria-label={lang === 'en' ? 'Close the diary' : 'Chiudi il diario'}
          data-testid="diario-modal-close"
          ref={closeBtnRef}
        >
          <X size={18} />
        </button>

        <div className="diario-paper" data-testid="diario-paper">
          <h1 id="diario-modal-title" className="diario-paper__title font-caveat">{d.title}</h1>

          {d.intro.map((p, i) => (
            <p key={`intro-${i}`} className="diario-paper__p font-caveat">{p}</p>
          ))}

          {d.entries.map((entry, idx) => (
            <article key={idx} className="diario-paper__entry">
              <header className="diario-paper__entry-head">
                <span className="diario-paper__date font-caveat">{entry.date}</span>
                <span className="diario-paper__dash" aria-hidden="true">—</span>
                <h2 className="diario-paper__entry-title font-caveat">{entry.title}</h2>
              </header>
              {entry.body.map((para, i) => (
                <p key={i} className="diario-paper__p font-caveat">{para}</p>
              ))}
            </article>
          ))}

          <p className="diario-paper__closing font-caveat">{d.closing}</p>

          <div className="diario-paper__sign">
            <SignatureSVG
              style={{
                width: 'min(300px, 60vw)',
                height: 'auto',
                color: '#3a2418',
                display: 'block',
              }}
            />
          </div>
        </div>

        <div className="diario-modal__footer">
          <button
            type="button"
            className="diario-modal__close-link"
            onClick={onClose}
            data-testid="diario-modal-close-bottom"
          >
            &larr; {lang === 'en' ? 'Back to site' : 'Torna al sito'}
          </button>
        </div>
      </div>
    </div>
  );
}
